import * as React from "react";
import { cn } from "../../lib/utils";

// Type for NumberFormat parts (not available in ES6 lib)
type NumberFormatPart = {
  type:
    | "currency"
    | "decimal"
    | "fraction"
    | "group"
    | "infinity"
    | "integer"
    | "literal"
    | "minus"
    | "nan"
    | "plus"
    | "percentSign"
    | "unit";
  value: string;
};

export interface AmountProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  /**
   * The numeric amount value to display
   */
  value: number;
  /**
   * Currency code (e.g., "USD", "INR", "EUR", "GBP")
   */
  currency?: string;
  /**
   * Locale for number formatting (default: "en-US")
   */
  locale?: string;
  /**
   * Minimum number of decimal places (default: 2)
   */
  minimumFractionDigits?: number;
  /**
   * Maximum number of decimal places (default: 2)
   */
  maximumFractionDigits?: number;
  /**
   * Whether to show the currency code/symbol (default: true)
   */
  showCurrency?: boolean;
  /**
   * Custom className for the component
   */
  className?: string;
}

/**
 * Amount component that displays formatted currency amounts with decimal numbers in superscript.
 *
 * @example
 * ```tsx
 * <Amount value={1234.56} currency="USD" />
 * // Renders: USD 1,234.<sup>56</sup>
 *
 * <Amount value={5000} currency="INR" />
 * // Renders: INR 5,000.<sup>00</sup>
 * ```
 */
const Amount = React.forwardRef<HTMLSpanElement, AmountProps>(
  (
    {
      value,
      currency,
      locale = "en-US",
      minimumFractionDigits = 2,
      maximumFractionDigits = 2,
      showCurrency = true,
      className,
      ...props
    },
    ref
  ) => {
    // Parse the formatted amount into parts
    const parts = React.useMemo(() => {
      // Always format as a number (not currency style) to get number formatting
      const formatter = new Intl.NumberFormat(locale, {
        minimumFractionDigits,
        maximumFractionDigits,
      });

      // Use formatToParts to get structured parts
      const formattedParts = (
        formatter as Intl.NumberFormat & {
          formatToParts(value: number): NumberFormatPart[];
        }
      ).formatToParts(value);

      let integerPart = "";
      let decimalPart = "";
      let decimalSeparator = ".";

      formattedParts.forEach((part: NumberFormatPart) => {
        switch (part.type) {
          case "integer":
            integerPart += part.value;
            break;
          case "decimal":
            decimalSeparator = part.value;
            break;
          case "fraction":
            decimalPart = part.value;
            break;
          case "group":
            integerPart += part.value;
            break;
        }
      });

      // Use currency code as prefix if provided and showCurrency is true
      const currencyPrefix = currency && showCurrency ? `${currency} ` : "";

      return {
        currency: currencyPrefix,
        integer: integerPart,
        decimal: decimalPart,
        decimalSeparator: decimalSeparator,
        hasDecimal: decimalPart.length > 0,
      };
    }, [
      value,
      currency,
      locale,
      minimumFractionDigits,
      maximumFractionDigits,
      showCurrency,
    ]);

    return (
      <span
        ref={ref}
        className={cn("inline-flex items-baseline", className)}
        {...props}
      >
        {parts.currency && <span>{parts.currency}</span>}
        <span className="ml-[0.2em]">{parts.integer}</span>
        {parts.hasDecimal && (
          <>
            <span className="mx-[0.05em]">{parts.decimalSeparator}</span>
            <sup className="text-[0.75em] leading-none">{parts.decimal}</sup>
          </>
        )}
      </span>
    );
  }
);

Amount.displayName = "Amount";

export { Amount };
